import {AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {AttachmentDetails} from '../../models/attachment-details.model';
import {AuthenticationService, InvoiceService, WorkspacesService} from '../../core/services';
import {environment} from '../../../environments/environment';
import {saveAs} from 'file-saver';

@Component({
  selector: 'app-invoice-visualising',
  templateUrl: './invoice-visualising.component.html',
  styleUrls: ['./invoice-visualising.component.scss']
})
export class InvoiceVisualisingComponent implements OnInit, AfterViewInit {
  invoiceId: string;
  workspaceId: string;
  public attachmentDetails: AttachmentDetails;
  private attachment: Blob;
  public pdfsrc: string;
  private cx: CanvasRenderingContext2D;
  private viewerWidth: number;

  @ViewChild('canvas', {static: false}) canvas: ElementRef;
  @ViewChild('documentViewer', {static: false}) documentViewer: ElementRef;
  @ViewChild('drawer', {static: false}) drawer: any;

  constructor(private route: ActivatedRoute,
              private authenticationService: AuthenticationService,
              private invoiceService: InvoiceService,
              private workspaceService: WorkspacesService) {
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.invoiceId = params.get('invoiceId');
      this.workspaceId = params.get('workspaceId');
      if (this.invoiceId && this.workspaceId) {
        this.workspaceService.setWorkspaceId(this.workspaceId);
        this.loadInvoiceAttachment();
      }
    });
  }

  ngAfterViewInit(): void {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    this.cx = canvasEl.getContext('2d');
  }

  private async loadInvoiceAttachment() {
    await this.getAttachmentDetailsFromApi(this.invoiceId);
    this.getAttachmentBinaryFromAPI(this.invoiceId);
  }

  private async getAttachmentDetailsFromApi(invoiceId: string) {
    try {
      const response = await this.invoiceService.getAttachmentDetailsFromAPI(invoiceId, 'original-file');
      this.attachmentDetails = response[0];
    } catch (error) {
      console.error('Error loading attachment details:', error);
    }
  }

  private getAttachmentBinaryFromAPI(
      invoiceId: string,
      callback = this.setAttachmentBinary.bind(this)
  ): void {
    const url = `${environment.backendUrl}/invoicestore/app/api/invoices/${invoiceId}/attachment-content`;
    const body = {attachmentType: 'original-file'};

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader(
        'Authorization',
        'bearer ' + this.authenticationService.getUserToken() + ''
    );

    xhr.onload = function() {
      if (this.status === 200) {
        callback(this.response);
      } else {
        console.error(xhr.statusText);
      }
    };

    xhr.send(JSON.stringify(body));
  }

  private setAttachmentBinary(blob: Blob): void {
    this.attachment = blob;
    const {mimeType} = this.attachmentDetails;

    this.isAttachmentImage(mimeType)
        ? this.canvasDrawInit(blob)
        : this.createPDFString(blob);
  }

  private isAttachmentImage(mimeType: string): boolean {
    return (
        mimeType === 'image/png' ||
        mimeType === 'image/jpeg' ||
        mimeType === 'image/jpg'
    );
  }

  private createPDFString(attachment: Blob): void {
    this.pdfsrc = URL.createObjectURL(attachment);
  }

  private canvasDrawInit(attachment: Blob): void {
    const image = new Image();
    image.src = URL.createObjectURL(attachment);
    this.viewerWidth = this.documentViewer.nativeElement.offsetWidth - 4;
    image.onload = () => {
      this.cx.canvas.width = this.viewerWidth;
      this.cx.canvas.height =
          (this.viewerWidth * image.naturalHeight) / image.naturalWidth;
      this.cx.drawImage(
          image,
          0,
          0,
          this.cx.canvas.width,
          this.cx.canvas.height
      );
    };
  }

  public downloadAttachment(): void {
    if (this.attachment && this.attachmentDetails) {
      const {mimeType, originalFileName} = this.attachmentDetails;
      const blob = new Blob([this.attachment], {
        type: mimeType,
      });
      saveAs(blob, originalFileName);
    } else {
      console.error('No attachment available to download');
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (this.attachmentDetails && this.isAttachmentImage(this.attachmentDetails.mimeType)) {
      this.canvasDrawInit(this.attachment);
    }
  }
}
